import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap } from './client';

export const useMe = () => useQuery({ queryKey: ['me'], queryFn: async () => unwrap(await api.GET('/api/v1/me')), staleTime: 60_000 });

export const useAgents = () => useQuery({ queryKey: ['agents'], queryFn: async () => unwrap(await api.GET('/api/v1/agents')) });

export const useAgent = (id: string) =>
  useQuery({ queryKey: ['agents', id], queryFn: async () => unwrap(await api.GET('/api/v1/agents/{id}', { params: { path: { id } } })) });

export const useServices = () => useQuery({ queryKey: ['services'], queryFn: async () => unwrap(await api.GET('/api/v1/services')) });

export const usePositions = () => useQuery({ queryKey: ['positions'], queryFn: async () => unwrap(await api.GET('/api/v1/positions')) });

export const useDecrees = () => useQuery({ queryKey: ['decrees'], queryFn: async () => unwrap(await api.GET('/api/v1/decrees')) });

export const useCatalog = (name: 'agent-statuses' | 'decree-types' | 'career-event-types' | 'absence-types') =>
  useQuery({
    queryKey: ['catalogs', name],
    queryFn: async () => unwrap(await api.GET('/api/v1/catalogs/{name}', { params: { path: { name } } })),
    staleTime: 5 * 60_000,
  });

export const useAudit = (limit = 100) =>
  useQuery({ queryKey: ['audit', limit], queryFn: async () => unwrap(await api.GET('/api/v1/audit', { params: { query: { limit } } })) });

export function useRevealSensitive() {
  return useMutation({
    mutationFn: async (id: string) => unwrap(await api.GET('/api/v1/agents/{id}/sensitive', { params: { path: { id } } })),
  });
}

export function useCreateDecree() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      agentId: string;
      typeId: string;
      subject: string;
      visas: string[];
      articles: string[];
      effectiveOn?: string | null;
    }) => unwrap(await api.POST('/api/v1/decrees', { body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['decrees'] }),
  });
}

export function useSignDecree() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, signedOn, signedBy }: { id: string; signedOn: string; signedBy: string }) =>
      unwrap(await api.POST('/api/v1/decrees/{id}/sign', { params: { path: { id } }, body: { signedOn, signedBy } })),
    onSuccess: (_d, v) => {
      void qc.invalidateQueries({ queryKey: ['decrees'] });
      void qc.invalidateQueries({ queryKey: ['agents'] });
      void qc.invalidateQueries({ queryKey: ['audit'] });
      void v;
    },
  });
}

export function useVerifyAudit() {
  return useMutation({ mutationFn: async () => unwrap(await api.GET('/api/v1/audit/verify')) });
}
